import React, { useEffect, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageHeader from '../components/account/PageHeader';
import PhotoStrip from '../components/review/PhotoStrip';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { Booking } from '../data/bookings';
import {
  MyReview,
  reviewMaxChars,
  reviewMaxPhotos,
  ReviewPhoto,
  REVIEW_TAGS,
} from '../data/reviews';
import { captureWithCamera, pickFromLibrary } from '../services/photos';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Rating a finished job, as its own page.
 *
 * This replaces `ReviewSheet` for composing and editing. The sheet rose over
 * whatever screen was open with that screen visible through the scrim, which
 * tested badly on the edit path in particular: the page behind - the account,
 * the ratings list - read as where you were, and the sheet as an interruption
 * over it. The form is long enough to be a destination (stars, tags, words,
 * photographs), so it gets a page, a header and a Back that lands exactly
 * where it was opened from - the opener never unmounts underneath it.
 *
 * ---------------------------------------------------------------------------
 * THE STARS ARE THE ONLY REQUIRED FIELD
 * ---------------------------------------------------------------------------
 * Everything else is optional and looks optional. Most people will tap five
 * stars and leave, and a form that blocks them until they have written a
 * sentence collects fewer ratings, not better ones.
 *
 * One review per booking, so this is also the editor: given an existing
 * review the page opens filled in and its button says "Update review".
 */

const STARS = [1, 2, 3, 4, 5];

/** What each star means, so the scale is not left to interpretation. */
const STAR_WORDS = ['', 'Poor', 'Not great', 'Fine', 'Good', 'Excellent'];

type Props = {
  booking: Booking;
  /** Their existing review, if they have already left one. */
  existing?: MyReview;
  onBack: () => void;
  onSubmit: (review: MyReview) => void;
};

function ReviewScreen({ booking, existing, onBack, onSubmit }: Props) {
  const resp = useResponsive();
  const insets = useSafeAreaInsets();

  const [stars, setStars] = useState(existing?.stars ?? 0);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const [tags, setTags] = useState<string[]>(existing?.tags ?? []);
  const [photos, setPhotos] = useState<ReviewPhoto[]>(existing?.photos ?? []);
  /*
   * Shown the first time somebody taps a locked star, and then left up.
   *
   * A control that simply ignores a tap is the worst of the three options
   * here: the customer cannot tell it from a tap that missed, so they tap
   * again, harder, and conclude the app is broken. Saying why costs one line.
   */
  const [ratingTouched, setRatingTouched] = useState(false);
  /** Set when Android has refused the camera for good - see `attach`. */
  const [cameraBlocked, setCameraBlocked] = useState(false);

  /* The page is mounted per composing session, but guard against the caller
     swapping the booking underneath it - reset to whatever is being edited. */
  useEffect(() => {
    setStars(existing?.stars ?? 0);
    setComment(existing?.comment ?? '');
    setTags(existing?.tags ?? []);
    setPhotos(existing?.photos ?? []);
    setRatingTouched(false);
    setCameraBlocked(false);
  }, [booking.id, existing]);

  /* One list for every trade now. The server has no category dimension to
     count a per-trade tag against - see `REVIEW_TAGS`. */
  const options = REVIEW_TAGS;

  const toggleTag = (tag: string) =>
    setTags(current =>
      current.includes(tag)
        ? current.filter(item => item !== tag)
        : [...current, tag],
    );

  /*
   * Both sources, one landing place. The picking itself lives in
   * `services/photos.ts` - the downscale, the camera permission and the cap
   * are the same wherever a photograph is attached.
   */
  const attach = async (from: 'camera' | 'library') => {
    const room = reviewMaxPhotos() - photos.filter(p => !p.hidden).length;
    if (room <= 0) {
      return;
    }

    const result =
      from === 'camera'
        ? await captureWithCamera(room)
        : await pickFromLibrary(room);

    /* A permanently refused camera is the one outcome worth saying out loud:
       nothing on this page can fix it, so it points at the place that can. */
    setCameraBlocked(result.blocked === 'camera-permission');

    if (result.photos.length) {
      setPhotos(current => [...current, ...result.photos]);
    }
  };

  const remaining = reviewMaxChars() - comment.length;
  const ready = stars > 0;

  /*
   * ---------------------------------------------------------------------------
   * A RATING IS WRITTEN ONCE. THE WORDS ARE NOT.
   * ---------------------------------------------------------------------------
   * The server's rule, not this screen's invention: a second rating on the same
   * booking is a 409 - "this booking has already been rated, and a rating
   * cannot be changed" - while the comment, the tags and the photos are updated
   * happily.
   *
   * This screen used to let the stars move anyway. The customer changed 4 to 5,
   * tapped Update, waited, and got an error about something they had done
   * twenty seconds earlier and could no longer undo without remembering what
   * the old value had been. The refusal belongs at the tap, where it is still
   * one decision, and not at the end of a form.
   */
  const ratingLocked = !!existing?.stars;

  const submit = () => {
    if (!ready) {
      return;
    }
    onSubmit({
      bookingId: booking.id,
      service: booking.title,
      icon: existing?.icon ?? 'star',
      when: booking.when,
      stars,
      comment: comment.trim(),
      tags,
      photos,
      edited: !!existing,
    });
  };

  return (
    <View style={styles.root}>
      <PageHeader
        title={existing ? 'Edit your review' : 'Rate your service'}
        onBack={onBack}
      />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text variant="caption" align="center" color={colors.inkMuted}>
          {booking.title}
        </Text>

        {/* --------------------------------------------------------- stars */}
        <View style={styles.stars}>
          {STARS.map(star => (
            <Pressable
              key={star}
              accessibilityRole="button"
              accessibilityLabel={`${star} star${star > 1 ? 's' : ''}`}
              accessibilityState={{
                selected: star <= stars,
                disabled: ratingLocked,
              }}
              hitSlop={6}
              /*
               * Still pressable while locked, deliberately. `disabled` would
               * swallow the tap and tell nobody anything; this catches it and
               * answers.
               */
              onPress={() =>
                ratingLocked ? setRatingTouched(true) : setStars(star)
              }
              style={({ pressed }) =>
                pressed && !ratingLocked ? styles.pressed : null
              }
            >
              <Icon
                name={star <= stars ? 'starFilled' : 'star'}
                size={34}
                color={star <= stars ? colors.rating : colors.borderStrong}
              />
            </Pressable>
          ))}
        </View>

        {/* Only once they have tried. Saying it up front would be a warning
            about something most people were never going to do. */}
        {ratingLocked && ratingTouched ? (
          <Text variant="caption" align="center" color={colors.inkMuted}>
            Your rating cannot be changed. You can still edit your review and
            photos.
          </Text>
        ) : null}

        {/* Held open whether or not a star is picked, so the page does not
            jump under the finger that just tapped one. */}
        <Text variant="labelSm" align="center" color={colors.inkBody}>
          {STAR_WORDS[stars] || ' '}
        </Text>

        {/* ---------------------------------------------------------- tags */}
        {stars > 0 ? (
          <View style={styles.block}>
            <Text variant="labelSm" color={colors.inkBody}>
              What stood out?
            </Text>
            <View style={styles.tags}>
              {options.map(tag => {
                const on = tags.includes(tag.key);
                return (
                  <Pressable
                    key={tag.key}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    accessibilityLabel={tag.label}
                    onPress={() => toggleTag(tag.key)}
                    style={({ pressed }) => [
                      styles.tag,
                      on ? styles.tagOn : null,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    <Text
                      variant="caption"
                      color={on ? colors.onPrimary : colors.inkBody}
                    >
                      {tag.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ) : null}

        {/* ------------------------------------------------------- comment */}
        <View style={styles.block}>
          <Text variant="labelSm" color={colors.inkBody}>
            Anything else?{' '}
            <Text variant="caption" color={colors.inkMuted}>
              Optional
            </Text>
          </Text>
          <TextInput
            value={comment}
            onChangeText={next => setComment(next.slice(0, reviewMaxChars()))}
            placeholder="What went well, or what could have been better"
            placeholderTextColor={colors.inkSubtle}
            multiline
            style={styles.input}
            accessibilityLabel="Your review"
          />
          {/* Only near the ceiling. A counter from the first character is a
              target, and it makes a short honest review feel unfinished. */}
          {remaining <= 50 ? (
            <Text variant="caption" align="right" color={colors.inkMuted}>
              {remaining} characters left
            </Text>
          ) : null}
        </View>

        {/* -------------------------------------------------------- photos */}
        <View style={styles.block}>
          <PhotoStrip
            photos={photos}
            max={reviewMaxPhotos()}
            onAdd={() => attach('library')}
            onCapture={() => attach('camera')}
            onRemove={photo =>
              setPhotos(current => current.filter(item => item.id !== photo.id))
            }
          />

          {cameraBlocked ? (
            <Text variant="caption" color={colors.danger}>
              Camera access is off for Homingo. Turn it on in your phone's
              Settings, or upload a photo from your library instead.
            </Text>
          ) : null}
        </View>

        <View style={styles.note}>
          <Icon name="shieldCheck" size={14} color={colors.primary} />
          <Text
            variant="caption"
            color={colors.inkMuted}
            style={styles.noteText}
          >
            Your photos appear on your review and on the professional's profile.
            You can take any of them down later.
          </Text>
        </View>
      </ScrollView>

      {/* Pinned below the form, outside the scroller, so the one control that
          finishes the page never has to be hunted for. */}
      <View
        style={[
          styles.footer,
          { paddingBottom: Math.max(insets.bottom, spacing.md) },
        ]}
      >
        <Text variant="caption" align="center" color={colors.inkMuted}>
          {ready
            ? 'Your name and rating are shown publicly.'
            : 'Pick a rating to continue.'}
        </Text>
        <Button
          label={existing ? 'Update review' : 'Submit review'}
          disabled={!ready}
          onPress={submit}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
    gap: spacing.sm,
  },
  stars: {
    flexDirection: 'row',
    alignSelf: 'center',
    gap: spacing.sm,
    paddingTop: spacing.md,
  },
  block: {
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  tag: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  tagOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  input: {
    ...inputText,
    minHeight: 96,
    textAlignVertical: 'top',
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  note: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  noteText: {
    flex: 1,
  },
  footer: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default ReviewScreen;
